import { alpha, createTheme, darken } from '@mui/material'

// Color setup
const mainColor = '#2ecc71' // Terminal Green

const darkBackground = '#0d1117'
const darkPrimaryMain = '#2ecc71'
const darkPrimaryLight = '#82e0aa'
const darkSecondary = '#607d8b'
const darkError = '#f44336'
const darkWarning = '#ff9800'
const darkInfo = '#2196f3'
const darkSuccess = '#4caf50'

const lightBackground = '#f4f6f8'
const lightPrimaryMain = '#1b5e20' // Deep Green
const lightPrimaryLight = '#4caf50'
const lightSecondary = '#455a64'
const lightError = '#d32f2f'
const lightWarning = '#ed6c02'
const lightInfo = '#0288d1'
const lightSuccess = '#2e7d32'
//

export const logInBackgroundImage = `radial-gradient(circle at 50% 14em, ${darkBackground} 0%, #000000 70%, #000000 100%)`

const darkPalette = {
  background: { default: darkBackground, paper: '#161b22' },
  primary: { main: darkPrimaryMain, light: darkPrimaryLight },
  secondary: { main: darkSecondary },
  error: { main: darkError },
  warning: { main: darkWarning },
  info: { main: darkInfo },
  success: { main: darkSuccess },
  mode: 'dark',
}

const lightPalette = {
  background: { default: lightBackground, paper: '#ffffff' },
  primary: { main: lightPrimaryMain, light: lightPrimaryLight },
  secondary: { main: lightSecondary },
  error: { main: lightError },
  warning: { main: lightWarning },
  info: { main: lightInfo },
  success: { main: lightSuccess },
  text: {
    primary: '#1a1a1a',
    secondary: '#454545',
  },
  mode: 'light',
}

const componentsOverrides = (theme) => ({
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: theme.palette.mode === 'dark' ? '#010409' : theme.palette.primary.main,
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 4,
        textTransform: 'none',
        fontWeight: 600,
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.primary.main,
        },
      },
      input: {
        padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      head: {
        backgroundColor: theme.palette.mode === 'dark' ? '#010409' : theme.palette.grey[100],
        fontWeight: 700,
        textTransform: 'uppercase',
        fontSize: '0.75rem',
        letterSpacing: '0.05em',
      },
    },
  },
  RaAppBar: {
    styleOverrides: {
      root: {
        color: theme.palette.text.primary,
        '& .RaAppBar-toolbar': {
          backgroundColor: theme.palette.mode === 'dark' ? '#0d1117' : theme.palette.primary.main,
          color: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#ffffff',
          backgroundImage: 'none',
        },
      },
    },
  },
  RaMenuItemLink: {
    styleOverrides: {
      root: {
        padding: '10px 16px',
        margin: '4px 8px',
        '&:hover': {
          borderRadius: 4,
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
        },
        '&.RaMenuItemLink-active': {
          borderRadius: 4,
          backgroundColor: alpha(theme.palette.primary.main, 0.12),
          color: theme.palette.primary.main,
          '& .MuiSvgIcon-root': {
            fill: theme.palette.primary.main,
          },
          '&:after': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: '20%',
            height: '60%',
            width: 3,
            backgroundColor: theme.palette.primary.main,
            borderRadius: '0 4px 4px 0',
          },
        },
      },
    },
  },
})

const createCustomTheme = (palette) => {
  const themeOptions = {
    palette,
    shape: { borderRadius: 4 },
    sidebar: { width: 240, closedWidth: 60 },
    spacing: 8,
    typography: {
      fontFamily: 'Gabarito, "Roboto Mono", monospace, sans-serif',
      h1: { fontWeight: 600 },
      h2: { fontWeight: 600 },
      h3: { fontWeight: 600 },
      h4: { fontWeight: 600 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
  }
  const theme = createTheme(themeOptions)
  theme.components = componentsOverrides(theme)
  return theme
}


export const customLightTheme = createCustomTheme(lightPalette)
export const customDarkTheme = createCustomTheme(darkPalette)
